import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { HiLocationMarker, HiMail, HiPhone } from 'react-icons/hi';
import { BsSend } from 'react-icons/bs';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactUs = () => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, form);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 py-12" data-testid="contact-page">
      <Helmet>
        <title>Contact Us | Explore MHC - Martinsville & Henry County, VA</title>
        <meta name="description" content="Get in touch with Explore MHC. Questions about listing your business or event in Martinsville and Henry County, Virginia? We're here to help." />
        <link rel="canonical" href="https://exploremhc.com/contact-us" />
      </Helmet>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Contact Us
          </h1>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
            Have questions about Explore MHC? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="lg:col-span-1 space-y-6">
            {/* Email Card */}
            <div className="bg-white rounded-2xl p-6 border border-neutral-200 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <HiMail className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-bold text-lg text-neutral-900 mb-2">Email Us</h3>
              <p className="text-neutral-600 mb-3">Send us an email anytime</p>
              <a href="mailto:hello@exploremhc.com" className="text-primary font-semibold hover:underline">
                hello@exploremhc.com
              </a>
            </div>

            {/* Location Card */}
            <div className="bg-white rounded-2xl p-6 border border-neutral-200 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center mb-4">
                <HiLocationMarker className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="font-bold text-lg text-neutral-900 mb-2">Location</h3>
              <p className="text-neutral-600">
                Serving the Martinsville &<br />
                Henry County, Virginia community
              </p>
            </div>

            {/* Business Inquiries Card */}
            <div className="bg-gradient-to-br from-accent/10 to-accent/5 rounded-2xl p-6 border border-accent/20">
              <h3 className="font-bold text-lg text-neutral-900 mb-2">Business Inquiries</h3>
              <p className="text-neutral-600 mb-3">
                Interested in featuring your business or advertising on Explore MHC?
              </p>
              <a href="mailto:hello@exploremhc.com?subject=Business Inquiry" className="text-accent font-semibold hover:underline">
                Get in touch →
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-8 border border-neutral-200">
              <h2 className="text-2xl font-bold text-neutral-900 mb-6" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Send us a Message
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">Your Name *</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="form-input"
                      placeholder="John Smith"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="form-label">Email Address *</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="form-input"
                      placeholder="john@example.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label">Subject *</label>
                  <input
                    type="text"
                    required
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="form-input"
                    placeholder="How can we help?"
                    data-testid="contact-subject-input"
                  />
                </div>

                <div>
                  <label className="form-label">Message *</label>
                  <textarea
                    required
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="form-textarea"
                    rows={6}
                    placeholder="Tell us more about your inquiry..."
                    data-testid="contact-message-textarea"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex items-center justify-center gap-2 w-full md:w-auto"
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    'Sending...'
                  ) : (
                    <>
                      <BsSend className="w-4 h-4" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
